import React, { useState, useEffect } from "react";
import api from "../../services/api";

/*
 * System-generated activity types are written by the backend
 * and must not appear in the manual Add Activity dropdown.
 *
 * The DB exposes a hide_from_user flag on each row. We trust
 * that flag as the source of truth. The hardcoded fallback
 * list is used only if the API returns nothing usable.
 */
const FALLBACK_ACTIVITY_TYPES = [
  { id: 1, name: "Phone Call" },
  { id: 2, name: "Email" },
  { id: 3, name: "Meeting" },
  { id: 4, name: "Follow-up" },
  { id: 5, name: "Note Added" },
];

function AddActivity({ onSave, onCancel, saving = false }) {
  const [activityTypes, setActivityTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    type: "",
    description: "",
    scheduledDate: new Date().toISOString().slice(0, 16),
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadActivityTypes();
  }, []);

  const loadActivityTypes = async () => {
    setLoading(true);
    try {
      const data = await api.getMasterData("activityTypes");
      const list = Array.isArray(data) ? data : [];

      // Only user-selectable types
      const userSelectable = list.filter(
          (t) => t?.name && !t.hideFromUser,
        );

      if (userSelectable.length > 0) {
        setActivityTypes(userSelectable);
      } else {
        setActivityTypes(FALLBACK_ACTIVITY_TYPES);
      }
    } catch (err) {
      console.error("Failed to load activity types:", err);
      setActivityTypes(FALLBACK_ACTIVITY_TYPES);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.type) newErrors.type = "Activity type is required";
    const trimmedDesc = String(formData.description || "").trim();
    if (!trimmedDesc) {
      newErrors.description = "Description is required";
    } else if (trimmedDesc.length > 2000) {
      newErrors.description = "Description cannot exceed 2000 characters";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave({ ...formData, description: trimmedDesc });
  };

  if (loading) {
    return (
      <div className="loading-spinner" style={{ padding: "20px" }}>
        Loading activity types...
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="add-activity-form">
      <div className="form-row">
        <div className="form-group">
          <label>Activity Type *</label>
          <select
            name="type"
            className={`form-control ${errors.type ? "error" : ""}`}
            value={formData.type}
            onChange={handleChange}
            disabled={saving}
          >
            <option value="">Select type</option>
            {activityTypes.map((type) => (
              <option key={type.id} value={type.name}>
                {type.name}
              </option>
            ))}
          </select>
          {errors.type && <div className="error-text">{errors.type}</div>}
        </div>
      </div>
      <div className="form-group">
        <label>Description *</label>
        <textarea
          name="description"
          className={`form-control ${errors.description ? "error" : ""}`}
          rows="3"
          value={formData.description}
          onChange={handleChange}
          placeholder="Enter activity details..."
          disabled={saving}
          maxLength={2000}
        />
        <div className="field-hint" style={{ fontSize: "0.68rem", color: "#8a95a3", marginTop: 4 }}>
          {String(formData.description || "").length}/2000
        </div>
        {errors.description && (
          <div className="error-text">{errors.description}</div>
        )}
      </div>
      <div className="form-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? (
            <>
              <i className="fas fa-spinner fa-spin"></i> Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save"></i> Save Activity
            </>
          )}
        </button>
      </div>
    </form>
  );
}

export default AddActivity;